import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule, MatOption, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { debounceTime, distinctUntilChanged, Observable, startWith, Subject, take, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import {
	ClientBusinessEntityControllerService,
	ClientSaleControllerService,
	ManagementBusinessEntityControllerService,
	ManagementBusinessEntityResponse,
	ManagementSaleControllerService,
	SalesFilterRequest,
} from 'api-contract-notices';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

@Component({
	selector: 'app-sales-filters',
	standalone: true,
	imports: [
		MatCardModule,
		CommonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		TranslateModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatTooltipModule,
		MatIconModule,
		MatProgressSpinnerModule,
		MatButtonModule,
		MatDividerModule,
		MatAutocompleteModule,
		MatOptionModule,
		MatInputModule,
		MatSelectModule,
	],
	templateUrl: './sales-filters.component.html',
	styleUrl: './sales-filters.component.scss',
})
export class SalesFiltersComponent implements OnInit, OnChanges, OnDestroy {
	private onDestroy: Subject<boolean> = new Subject();

	@Input() role!: 'admin' | 'client' | 'landing';
	@Input() dataAdministrationType!: 'SALES';
	@Input() resetFilters: any;
	@Input() apiLoading!: boolean;
	@Output() actionRequested: EventEmitter<any> = new EventEmitter();

	readonly INSTANT_SEARCH = false; // setting option to have search without button

	filtersFormGroup: FormGroup = new FormGroup({});

	DAYS_RANGE = 0;
	DATE_RANGE_LIMITS = {
		dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
		dateTo: new Date(),
	};
	filteredTrustees: ManagementBusinessEntityResponse[] | undefined;
	filteredDebtors: ManagementBusinessEntityResponse[] | undefined;
	allLocations: string[] = [];
	filteredLocations: string[] | undefined;

	saleTypes: SalesFilterRequest.TypesEnum[] = ['REAL_ESTATE', 'MOVABLE_PROPERTY', 'REAL_MOVABLE', 'BUILDABLE_LAND', 'AMENDMENT', 'LEASE_RENTAL'];
	@ViewChild('selectAllSaleTypes') private selectAllSaleTypes!: MatOption;

	constructor(
		private fb: FormBuilder,
		public utils: UtilsService,
		private sharedService: SharedService,
		private apiBusinessEntity: ManagementBusinessEntityControllerService,
		private apiManagementSale: ManagementSaleControllerService,
		private apiClientBusinessEntity: ClientBusinessEntityControllerService,
    private apiClientSale: ClientSaleControllerService
	) {}

	ngOnInit(): void {
		this.formInitializer();
		this.getLocations();

		this.sharedService.requestActionInComponent.pipe(takeUntil(this.onDestroy)).subscribe({
			next: response => {
				if (response.componentName === 'SalesFiltersComponent') {
					switch (response.action) {
						case 'FILTER_TRIGGER': {
							this.filterListItems();
							break;
						}
					}
				}
			},
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['resetFilters'] && !changes['resetFilters'].currentValue && !changes['resetFilters'].firstChange) {
			this.resetForm();
		}
		if (changes['dataAdministrationType'] && !changes['dataAdministrationType'].firstChange) {
			this.resetForm();
		}
		if (changes['role'] && changes['role'].currentValue === 'client') {
			this.DAYS_RANGE = 7;
			this.DATE_RANGE_LIMITS = {
				dateFrom: new Date(new Date().setDate(new Date().getDate() - this.DAYS_RANGE)),
				dateTo: new Date(),
			};
		}
	}

	ngOnDestroy(): void {
		this.onDestroy.next(true);
	}

	private formInitializer() {
		this.filtersFormGroup = this.fb.group({
			createdAtFrom: [this.DATE_RANGE_LIMITS.dateFrom],
			createdAtTo: [this.DATE_RANGE_LIMITS.dateTo],
			trusteeIds: [],
			debtorIds: [],
			location: [],
			types: [[]],
		});
		this.formControlWatchers();
	}

	saleTypeSelection(itemSelected?: Event) {
		if (itemSelected) {
			if (this.selectAllSaleTypes.selected) {
				this.selectAllSaleTypes.deselect();
			} else if (this.filtersFormGroup.controls['types'].value.length === this.saleTypes.length) {
				this.selectAllSaleTypes.select();
			}
		} else {
			this.filtersFormGroup.controls['types'].patchValue(this.selectAllSaleTypes.selected ? ['ALL', ...this.saleTypes] : [], 0);
		}
	}

	private formControlWatchers() {
		if (this.filtersFormGroup.controls['trusteeIds']) {
			this.filtersFormGroup.controls['trusteeIds'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				if (this.filtersFormGroup.controls['trusteeIds'].dirty) {
					this.searchBusinessEntities(searchTerm, 'trustee');
				}
			});
		}
		if (this.filtersFormGroup.controls['debtorIds']) {
			this.filtersFormGroup.controls['debtorIds'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				if (this.filtersFormGroup.controls['debtorIds'].dirty) {
					this.searchBusinessEntities(searchTerm, 'debtor');
				}
			});
		}

		if (this.filtersFormGroup.controls['location']) {
			this.filtersFormGroup.controls['location'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe(searchTerm => {
				if (this.filtersFormGroup.controls['location'].dirty) {
					if (typeof searchTerm === 'string' && searchTerm.length > 0) {
						const convertedTerm = this.utils.autoConvertLatinToCyrillic(searchTerm);
						if (convertedTerm !== searchTerm) {
							this.filtersFormGroup.controls['location'].setValue(convertedTerm, { emitEvent: false });
							this.searchLocations(convertedTerm);
						} else {
							this.searchLocations(searchTerm);
						}
					} else {
						this.searchLocations(searchTerm);
					}
				}
			});
		}
	}

	resetForm(formDirective?: FormGroupDirective) {
		this.filtersFormGroup.reset();

		if (formDirective) {
			formDirective.resetForm();
		}

		this.filtersFormGroup.controls['createdAtFrom'].setValue(this.DATE_RANGE_LIMITS.dateFrom);
		this.filtersFormGroup.controls['createdAtTo'].setValue(this.DATE_RANGE_LIMITS.dateTo);

		this.filterListItems();
	}

	filterListItems() {
		const filterDto = JSON.parse(JSON.stringify(this.filtersFormGroup.value));
		filterDto.createdAtFrom = this.utils.setDateToDay(this.filtersFormGroup.controls['createdAtFrom'].value, 'START');
		filterDto.createdAtTo = this.utils.setDateToDay(this.filtersFormGroup.controls['createdAtTo'].value, 'END');
		filterDto.trusteeIds = this.filtersFormGroup.controls['trusteeIds'].value?.id ? [this.filtersFormGroup.controls['trusteeIds'].value?.id] : [];
		filterDto.debtorIds = this.filtersFormGroup.controls['debtorIds'].value?.id ? [this.filtersFormGroup.controls['debtorIds'].value?.id] : [];
		const selectedTypes: string[] = this.filtersFormGroup.controls['types'].value || [];
		filterDto.types = selectedTypes.filter(t => t !== 'ALL');

		this.actionRequested.emit({ action: 'FILTER', filters: filterDto, id: 0 });
	}

	/** Filter input business entity name */
	private getBusinessEntitySearchObservable(searchTerm: string): Observable<any> {
		switch (this.role) {
			case 'admin':
				return this.apiBusinessEntity.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
			case 'client':
				return this.apiClientBusinessEntity.getClientBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
			default:
				return this.apiBusinessEntity.getBusinessEntitiesPageable({ businessEntity: searchTerm }, 0, 100);
		}
	}

	private searchBusinessEntities(searchTerm: string, filter: 'debtor' | 'trustee') {
		if (searchTerm && searchTerm.length > 0) {
			this.getBusinessEntitySearchObservable(searchTerm)
				.pipe(take(1))
				.subscribe({
					next: response => {
						if (filter === 'debtor') {
							this.filteredDebtors = response.content;
						} else if (filter === 'trustee') {
							this.filteredTrustees = response.content;
						}
					},
				});
		}
	}
	displayFn(company: ManagementBusinessEntityResponse): string {
		return company && company.id ? this.utils.getParameterByLanguageScript(company, 'name', true) : '';
	}

	//** Locations methods */
  private getLocationSearchObservable(): Observable<any> {
		switch (this.role) {
			case 'admin':
				return this.apiManagementSale.findAllLocationsAndPlacesInSales();
			case 'client':
				return this.apiClientSale.findAllLocationsAndPlacesInSalesClient();
			default:
				return this.apiManagementSale.findAllLocationsAndPlacesInSales();
		}
	}

	private getLocations() {
		this.getLocationSearchObservable()
			.pipe(take(1))
			.subscribe({
				next: response => {
					this.allLocations = response;
				},
			});
	}

	private searchLocations(searchTerm: string) {
		if (searchTerm && searchTerm.length > 0) {
			this.filteredLocations = this.allLocations.filter(l => l.toLowerCase().includes(searchTerm.toLowerCase()));
		} else {
			this.filteredLocations = [];
		}
	}
	//** (END)Locations methods */
}
